import { useEffect, useRef } from 'react';
import Image from 'next/image';

import { Box, Divider, Flex, Heading, LinkBox, LinkOverlay, Text } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { trackEvent } from '~/features/analytics/track-event';
import { FireIcon, RocketIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';

const VIEW_EVENT_VALUE = 'agentic-ai-ft-2026';
const CTA_HREF = '/course/agentic-ai--nd900';

const handleCtaClick = () =>
  trackEvent({
    name: 'Homepage CTA Clicked',
    type: 'click',
    label: 'agentic-ai-ft-2026 cta clicked',
  });

export function AgenticAiFreeTrialPromoCard() {
  const cardRef = useRef<HTMLDivElement>(null);
  const hasFiredViewRef = useRef(false);

  useEffect(() => {
    const el = cardRef.current;
    if (!el || hasFiredViewRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || hasFiredViewRef.current) return;
        hasFiredViewRef.current = true;
        trackEvent({
          type: 'view',
          name: 'Banner Viewed',
          label: 'hero_banner_viewed',
          value: VIEW_EVENT_VALUE,
        });
        observer.disconnect();
      },
      { threshold: 0.5 }
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  return (
    <LinkBox
      ref={cardRef}
      bgColor='black'
      borderRadius='8px'
      data-testid='agentic-ai-free-trial-promo-card'
      height='100%'
      minHeight='100%'
      overflow='hidden'
      padding={{ base: '32px', md: '48px' }}
      position='relative'
      width='100%'
    >
      <Box
        inset={0}
        pointerEvents='none'
        position='absolute'
        sx={{
          img: {
            objectFit: 'cover',
            objectPosition: { base: 'center', md: 'left center' },
          },
        }}
      >
        <Image alt='' quality={100} sizes='100vw' src='/images/experiments/ab_homepage_ft_2026/hero-bg.png' fill />
      </Box>
      <Box
        inset={0}
        pointerEvents='none'
        position='absolute'
        bgGradient={{
          base: 'linear(180deg, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.65) 100%)',
          md: 'linear(90deg, rgba(0, 0, 0, 0.92) 0%, rgba(0, 0, 0, 0.55) 60%, rgba(0, 0, 0, 0) 100%)',
        }}
      />

      <Flex alignItems='flex-start' flexDirection='column' gap={6} height='100%' position='relative' width='100%'>
        <Flex alignItems='center' gap={4} width='100%'>
          <Flex alignItems='center' gap={2}>
            <FireIcon color='orange.200' height='16px' width='16px' />
            <Text
              color='orange.200'
              fontFamily='condensed'
              fontSize='15px'
              fontWeight='500'
              letterSpacing='0.9px'
              lineHeight='1'
              textTransform='uppercase'
              whiteSpace='nowrap'
            >
              Most Popular
            </Text>
          </Flex>
          <Divider borderColor='rgba(255, 255, 255, 0.4)' flex={1} opacity={1} />
        </Flex>

        <Flex alignItems='flex-start' flexDirection='column' gap={3}>
          <Heading
            as='h2'
            color='white'
            fontSize={{ base: '1.75rem', md: '2.25rem' }}
            fontWeight='600'
            letterSpacing='-0.36px'
            lineHeight='1.1'
          >
            <LinkOverlay href={CTA_HREF} onClick={handleCtaClick}>
              Build AI systems that act.
            </LinkOverlay>
          </Heading>
          <Text color='gray.200' fontSize='md' lineHeight='1.5'>
            Join the over 19,000 learners who&rsquo;ve built thousands of AI agents and multi-agent systems.
          </Text>
        </Flex>

        <Box mt='auto' width={{ base: '100%', md: 'auto' }}>
          <ButtonLink
            buttonProps={{
              leftIcon: <RocketIcon height='20px' width='20px' />,
              colorScheme: 'blue',
              position: 'relative',
              zIndex: 1,
              width: { base: '100%', md: 'auto' },
              onClick: handleCtaClick,
            }}
            linkProps={{
              href: CTA_HREF,
            }}
          >
            Try For Free
          </ButtonLink>
        </Box>
      </Flex>
    </LinkBox>
  );
}
